import { DashboardLayout, PageContainer } from '@toolpad/core';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AuthenticatedRoutes from './rotues/AuthenticatedRoutes';
import { ReactRouterAppProvider } from '@toolpad/core/react-router';
import GlobalSnackbar from './components/GlobalSnackbar';
import { appTheme } from './theme';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupIcon from '@mui/icons-material/Group';
import CategoryIcon from '@mui/icons-material/Category';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import SpeedIcon from '@mui/icons-material/Speed';
import LoginPage from './resources/login/pages/LoginPage';
import UserAccount from './components/UserAccount';
import { useEffect } from 'react';
import { fetchUserRequest } from './api/user/userSlice';
import { sessionResolved } from './api/auth/authSlice';
import { authApiClient } from './api/client/axios';
import { useDispatch, useSelector } from 'react-redux';
import ForgotPasswordPage from './resources/forgotPassword/pages/ForgotPasswordPage';
import ScreenLoader from './components/ScreenLoader';
import NoSubscriptionAccessPage from './components/NoSubscriptionAccessPage';
import { PermissionModalProvider } from './context/PermissionModalProvider';
import { usePermissions } from './permissions/usePermissions';
import appConfig from './config/appConfig';
import EnvironmentBanner from './components/EnvironmentBanner';

const NAVIGATION = [
  {
    segment: 'organizations',
    title: 'Organization',
    icon: <GroupsIcon />,
    pattern: 'organizations{/:id}*',
  },
  {
    segment: 'partners',
    title: 'Partners',
    icon: <GroupIcon />,
    pattern: 'partners{/:id}*',
  },
  {
    segment: 'products',
    title: 'Products',
    icon: <CategoryIcon />,
    pattern: 'products{/:id}*',
  },
  {
    segment: 'subscriptions',
    title: 'Subscriptions',
    icon: <CardMembershipIcon />,
    pattern: 'subscriptions{/:id}*',
  },
  {
    segment: 'meters',
    title: 'Meters',
    icon: <SpeedIcon />,
    pattern: 'meters{/:id}*',
  },
];

const RedirectToLogin = () => {
  const location = useLocation();
  return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
};

function UserLoadingScreen() {
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

function AuthenticatedApp() {
  const { userLoading, hasAnySubscriptionPermission, getDefaultPath } = usePermissions();

  const defaultPath = getDefaultPath();
  const brandingHomeUrl = hasAnySubscriptionPermission() ? defaultPath : '/no-access';

  if (userLoading) {
    return <UserLoadingScreen />;
  }

  if (!hasAnySubscriptionPermission()) {
    return (
      <Routes>
        <Route path="*" element={<NoSubscriptionAccessPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <DashboardLayout
            sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}
            slots={{ toolbarAccount: UserAccount }}
            sidebarExpandedWidth={200}
            branding={{
              title: '',
              homeUrl: brandingHomeUrl,
              logo: <img src={appConfig.brandLogoPath} alt={`${appConfig.brandName} logo`} />,
            }}
          >
            <PageContainer maxWidth={'xxl'} breadcrumbs={[]} title="">
              <Routes>
                <Route path="/" element={<Navigate to={defaultPath} replace />} />
                {AuthenticatedRoutes()}
              </Routes>
            </PageContainer>
          </DashboardLayout>
        }
      />
    </Routes>
  );
}

const App = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const sessionChecked = useSelector((state) => state.auth.sessionChecked);
  const dispatch = useDispatch();

  useEffect(() => {
    authApiClient
      .get('/me', { skipSessionRedirect: true })
      .then(() => {
        dispatch(sessionResolved({ authenticated: true }));
      })
      .catch(() => {
        dispatch(sessionResolved({ authenticated: false }));
      });
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserRequest());
    }
  }, [dispatch, isAuthenticated]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
      }}
    >
      <EnvironmentBanner />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ReactRouterAppProvider theme={appTheme} navigation={NAVIGATION}>
          <PermissionModalProvider>
            {!sessionChecked ? (
              <ScreenLoader />
            ) : !isAuthenticated ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="*" element={<RedirectToLogin />} />
                </Routes>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <AuthenticatedApp />
              </Box>
            )}

            <ScreenLoader />
            <GlobalSnackbar />
          </PermissionModalProvider>
        </ReactRouterAppProvider>
      </Box>
    </Box>
  );
};

export default App;
